import React, { useContext, memo, useState, useEffect, useRef, useMemo } from "react";
import {
  Marker,
  MarkerClusterer
} from "@react-google-maps/api";
import { toast } from "react-toastify";
import axios from "axios";
import { WebMapContext } from "../WebMapGoogleMap";
import { SEARCH_AREA_STATES, SiteType, clusterDefaultZoomLvl, googleClusterImgUrl } from "../../../../constants/WebMap";
import { filterProposedEdotcoTowers, getTooltipDataForEdotcoTower } from "../../../../services/api/siteService";
import { paginatedApiInvoker } from "../../../../utils/webMapApiHelper";
import { convertArrayToStringWithDelimeter } from "../../../../utils/helper";

const SiteIcon = ({ idx = 1, iconData: item, onClickTower, mapTool, cluster = null }) => {
  return (
    <>
      {item && item.proposedTenants && item.proposedTenants.length > 0
        ? <Marker
          key={idx}
          position={{
            lat: item.latitude,
            lng: item.longitude,
          }}
          icon={{
            url: "/assets/edotcoProposed.svg",
            scaledSize: new window.google.maps.Size(36, 36),
            anchor: new window.google.maps.Point(18, 28),
          }}
          onClick={() => {
            onClickTower({
              lat: item.latitude,
              long: item.longitude,
              oprt: item.proposedTenants[0],
            });
          }}
          options={{
            clickable: mapTool ? false : true
          }}
          clusterer={cluster}
        />
        : <></>}
    </>
  );
}

const ProposedSitesSection = ({
  mapTool,
  proposedSiteVisible,
  edotcoSiteVisible,
  edotcoSubFilters = { status: [], action: [] },
  searchAreaBtn,
  setSearchAreaBtn,
  tooltipData,
  setEdotcoSiteTooltipData,
  setApisOngoing,
}) => {
  let cancelToken;
  let timeOutForApiCall = 600;
  const timeOutForProposedTowers = useRef();

  const {
    eyeIcons,
    siteZoom,
    mapZoomLevel,
    appliedFilters,
    selectedProposedOperator,
    edotcoSiteAreaToggle,
    geohashNE,
    geohashSW,
    boundsChanged,
    getBbox,
    downloadData, 
    setDownloadData,
  } = useContext(WebMapContext);

  const [proposedTowers, setProposedTowers] = useState([]);

  useEffect(() => {
    if (mapZoomLevel < Math.min(siteZoom, clusterDefaultZoomLvl)) setProposedTowers([]);
  }, [mapZoomLevel, siteZoom]);

  useEffect(() => {
    setProposedTowers([]);
  }, [appliedFilters.country, appliedFilters.year, appliedFilters.quarter]);

  useEffect(() => {
    if (edotcoSiteAreaToggle) setProposedTowers([]);
  }, [appliedFilters.level, appliedFilters.levelVal]);

  useEffect(() => {
    if (tooltipData && tooltipData.siteType === SiteType.Proposed) {
      setEdotcoSiteTooltipData()
    }
  }, [proposedTowers.length]);

  useEffect(() => {
    if (selectedProposedOperator.operatorList?.length <= 1 && selectedProposedOperator.value) return; // prevent api call if logic present but no multi oprts 
    if (common_cond) {
      filterProposedTowers();
    } else {
      setProposedTowers([]);
    }
    // eslint-disable-next-line
  }, [
    proposedSiteVisible,
    eyeIcons.sites,
    edotcoSiteVisible,
    selectedProposedOperator,
    edotcoSubFilters,
    edotcoSubFilters.action,
    edotcoSubFilters.status,
    geohashSW,
    edotcoSiteAreaToggle,
    siteZoom,
    boundsChanged
  ]);

  useEffect(() => {
    if (common_cond && searchAreaBtn === SEARCH_AREA_STATES.FETCH) {
      filterProposedTowers();
    }
    // eslint-disable-next-line
  }, [searchAreaBtn]);

  const common_cond = useMemo(() => {
    return proposedSiteVisible && eyeIcons.sites && edotcoSiteVisible && (mapZoomLevel >= siteZoom || mapZoomLevel >= clusterDefaultZoomLvl) &&
      edotcoSubFilters.status.length > 0 && edotcoSubFilters.action.length > 0;
  }, [eyeIcons.sites, proposedSiteVisible, edotcoSiteVisible, mapZoomLevel, siteZoom, edotcoSubFilters]);

  const filterProposedTowers = async () => {
    if (typeof cancelToken != typeof undefined) {
      cancelToken.cancel("Operation canceled due to new request.");
    }
    //Save the cancel token for the current request
    cancelToken = axios.CancelToken.source();
    clearTimeout(timeOutForProposedTowers.current);

    timeOutForProposedTowers.current = setTimeout(async () => {
      setProposedTowers([]);
      try {
        const checkedStatus = edotcoSubFilters.status
          .map((item) => {
            if (item.isChecked) return item.filterName;
            return null;
          })
          .filter((item) => item);

        const checkedActions = edotcoSubFilters.action
          .map((item) => {
            if (item.isChecked) return item.filterName;
            return null;
          })
          .filter((item) => item);
        const { neBound, swBound } = getBbox;

        let operatorList = selectedProposedOperator?.operatorList || [];

        const payload = {
          ...appliedFilters,
          adL: edotcoSiteAreaToggle && appliedFilters.level !== 'geoHashLevel' ? appliedFilters.level : null,
          adLVal: edotcoSiteAreaToggle && appliedFilters.level !== 'geoHashLevel' ? appliedFilters.levelVal : null,
          operators: convertArrayToStringWithDelimeter(operatorList, "_"),
          logicGate: selectedProposedOperator.value,
          dataType: edotcoSubFilters?.dataType,
          connectionType: edotcoSubFilters?.connectionType,
          status: convertArrayToStringWithDelimeter(checkedStatus, "_"),
          action: convertArrayToStringWithDelimeter(checkedActions, "_"),
          northEast: convertArrayToStringWithDelimeter(edotcoSiteAreaToggle && geohashNE.length ? geohashNE : neBound, "_"),
          southWest: convertArrayToStringWithDelimeter(edotcoSiteAreaToggle && geohashSW.length ? geohashSW : swBound, "_"),
        }

        await paginatedApiInvoker(
          (pageSize, page) => filterProposedEdotcoTowers(payload, cancelToken, pageSize, page),
          40000, // perPage
          () => setApisOngoing(count => count + 1),
          (data) => {
            if (data) setProposedTowers(prevState => [...prevState, ...data]);
          },
          () => {
            //Saving filters for download
            setDownloadData({
              ...downloadData,
              sitesProposed: {
               ...payload,
               cancelToken: cancelToken,
             },
            });
            if (!appliedFilters.level) setSearchAreaBtn(SEARCH_AREA_STATES.ENABLED);
            setApisOngoing(count => count - 1);
          }
        );
      } catch (err) {
        console.log("Error while filtering site towers", err);
        toast.error(err.message, { theme: "colored" });
      }
    }, timeOutForApiCall);
  };

  const onClickTower = async (params) => {
    getTooltipDataForEdotcoTower({
      countryCode: appliedFilters.countryCode,
      quarter: appliedFilters.quarter,
      year: appliedFilters.year,
      operator: params.oprt,
      lat: params.lat,
      long: params.long,
    })
      .then(({ success, data }) => {
        if (success) setEdotcoSiteTooltipData({ ...data, siteType: SiteType.Proposed });
        else toast.error(data, { theme: "colored" });
      })
      .catch((err) => {
        console.log(
          "Error wihle getting tool tip data for Proposed site tower",
          err
        );
        toast.error(err.message, { theme: "colored" });
      });
  };

  return (
    proposedTowers.length > 0 && eyeIcons.sites
      ? <div className="data-on-map">
        {mapZoomLevel >= siteZoom
          ? proposedTowers.map((item, index) => (
            <SiteIcon
              idx={index + "_" + item?.siteRef + "_edotco_proposed"}
              iconData={item}
              onClickTower={onClickTower}
              mapTool={mapTool}
            />
          ))
          : mapZoomLevel >= clusterDefaultZoomLvl
            ? <MarkerClusterer options={{ imagePath: googleClusterImgUrl }}>
              {(clusterer) => {
                return (
                  <>{proposedTowers.map((item, idx) => (
                    <SiteIcon
                      idx={idx}
                      iconData={item}
                      onClickTower={onClickTower}
                      mapTool={mapTool}
                      cluster={clusterer}
                    />
                  ))}</>
                )
              }}
            </MarkerClusterer>
            : <></>}
      </div>
      : <></>
  )
};

export default memo(ProposedSitesSection);